<template>
  <ReportPageLayout>
    <template #page-header>
      <OverviewNavHeader />
    </template>

    <!-- Default slot for main content -->
    <!-- Consider wrapping this section in DataStateHandler -->
    <div v-if="loading" class="d-flex justify-center align-center py-12">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </div>
    <div v-else-if="error && !report" class="error-container text-center py-8">
      <v-alert
        type="error"
        title="Error Loading Report"
        :text="error"
        class="mx-auto"
        max-width="600"
        variant="tonal"
        border="start"
      ></v-alert>
      <v-btn color="primary" class="mt-4" @click="reloadReport"> Retry </v-btn>
    </div>
    <v-responsive v-else-if="report" max-width="1320" class="mx-auto">
      <v-row>
        <v-col cols="12">
          <GradeHeadingCard
            :report="report"
            :selected-grade="String(route.params.grade)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="3">
          <GradeOverviewNav :grade="route.params.grade as string" />
        </v-col>
        <v-col cols="12" md="9">
          <div class="grade-overview-section">
            <SeriesPerformanceSnapshot
              title="Summary of Alignment and Usability"
              class="mb-8"
            />

            <GradeLevelSummaryRow
              :report="report"
              :selected-grade="String(route.params.grade)"
            />

            <!-- Grade Details -->
            <div class="d-flex justify-space-between align-center mb-4">
              <h2 class="text-h6 font-weight-bold text-secondary">
                ELA K-2 Overview
              </h2>
              <v-btn
                variant="text"
                color="primary"
                class="text-none"
                density="comfortable"
                @click.prevent="openGlossary('Gateway')"
              >
                Optional Link
              </v-btn>
            </div>

            <v-expand-transition>
              <div>
                <ReadMore>
                  {{ gradeAnalysis }}
                </ReadMore>
              </div>
            </v-expand-transition>

            <!-- Science of Reading Metrics -->
            <h2 class="text-h6 font-weight-bold text-secondary mt-10 mb-2">
              Publication Details
            </h2>

            <DataTable
              :headers="tableHeaders"
              :items="publicationDetailsData"
              textAlign="text-left"
              classes="mb-12"
              :skillName="false"
              setFontSize="14px"
            />
          </div>
        </v-col>
      </v-row>
    </v-responsive>
    <div v-else class="text-center py-8">
      <v-alert
        type="info"
        title="No Report Data"
        text="No grade overview data was found."
        class="mx-auto"
        max-width="600"
      ></v-alert>
    </div>
  </ReportPageLayout>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useRoute } from 'vue-router';
  import { API_BASE_URL } from '@/config';
  import { useGlossary } from '../composables/useGlossary';
  import { useReportLoader } from '../composables/useReportLoader';
  import { useDocumentTitle } from '../composables/useDocumentTitle';
  import ReportPageLayout from '../layouts/ReportPageLayout.vue';
  import ReadMore from '../components/shared/ReadMore.vue';
  import DataTable from '../components/shared/DataTable.vue';
  import GradeOverviewNav from '@/components/reportGrade/GradeOverviewNav.vue';
  import GradeLevelSummaryRow from '../components/reportGrade/GradeOverviewSummaryRow.vue';
  import SeriesPerformanceSnapshot from '../components/reports/SeriesPerformanceSnapshot.vue';
  import OverviewNavHeader from '../components/reportOverview/OverviewNavHeader.vue';
  import GradeHeadingCard from '../components/reportGrade/GradeHeadingCard.vue';

  const route = useRoute();
  const { openGlossary } = useGlossary();

  const reportSlug = computed(() => route.params.id as string);
  const { report, loading, error, reloadReport } = useReportLoader(reportSlug);

  const gradeAnalysis = computed(
    () =>
      `The instructional materials for Grade ${route.params.grade} demonstrate strong alignment...`,
  );

  const publicationDetailsData = ref([
    {
      title: 'Essential Resource Guide',
      isbn: '978-0-5477-2970-1',
      edition: 'Edition',
      publisher: 'Publisher',
      year: '2013',
    },
  ]);

  const tableHeaders = ['Title', 'ISBN', 'Edition', 'Publisher', 'Year'];

  const documentTitleState = computed(() => {
    if (loading.value) return 'Loading Report...';
    if (error.value) return 'Error Loading Report';
    if (report.value?.title)
      return `${report.value.title} - Grade ${route.params.grade}`;
    return 'Grade Overview';
  });

  useDocumentTitle(documentTitleState);
</script>

<style lang="scss">
  @use '../styles/abstracts/variables' as *;

  .v-responsive {
    padding-right: 0;
  }

  .content-section-title {
    font-family: 'rockwell', sans-serif !important;
    font-size: 38px !important;
    font-weight: normal !important;
  }

  .overview-section {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .content {
    color: rgb(var(--v-theme-text-secondary));
    font-size: 16px;
    line-height: 1.6;

    p {
      margin-bottom: 16px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .performance-table,
  .reading-metrics-table {
    width: 100%;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 4px;

    th {
      background-color: rgb(var(--v-theme-background));
      font-weight: bold;
      padding: 12px 16px;
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
    }

    td {
      padding: 12px 16px;
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
    }

    tr:last-child td {
      border-bottom: none;
    }

    tr:nth-child(even) {
      background-color: rgb(var(--v-theme-surface));
    }
  }

  .grade-level-summary {
    h4 {
      font-size: 16px;
    }

    .v-card {
      border: 1px solid rgba(var(--v-theme-border-light), 1);
    }
  }
</style>
